/**
 * v4 "Dukan" theme - bilingual settings + translations.
 * Supports English (LTR), Persian/Farsi (RTL), Dari (RTL), Pashto (RTL).
 *
 * Design intent: low-tech-literacy shopkeeper audience. Translations stay
 * plain - "money you made" not "revenue", "money you owe in tax" not
 * "liability". If a phrase has no shopkeeper-friendly equivalent in target
 * language, we lean toward simple over technically-precise.
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
// Note: v4 keeps its theme/settings local on purpose. It does NOT use the
// global ThemeContext that the firm admin panel uses, so toggling theme
// inside v4 doesn't affect the panel admin and vice versa. When v4 moves to
// its own subdomain (e.g. app.ronidoc.com.au) it will also have its own
// cookies + Cognito user pool - none of that is shared with the firm panel.

export type Lang = 'en' | 'fa';
export type Dir = 'ltr' | 'rtl';
export type FontSize = 'normal' | 'large' | 'xl';
export type ThemeMode = 'light' | 'dark';
export type Accent = 'green' | 'teal' | 'blue' | 'indigo' | 'violet' | 'rose' | 'amber' | 'graphite';

// 6+ accent palettes. Each provides the full set of shades we need in JSX.
// Default is RoniDoc green #2EAF7C - sits between emerald and teal, calm and
// trustworthy without feeling corporate-bank-blue.
export const ACCENTS: Record<Accent, { name: string; primary: string; hover: string; text: string; tint: string }> = {
  green:    { name: 'Green',    primary: '#35AD88', hover: '#1E9D77', text: '#1E9D77', tint: '#E8F8F1', light: '#48D0A6' } as any,
  teal:     { name: 'Teal',     primary: '#14B8A6', hover: '#0F9C8C', text: '#0F766E', tint: '#F0FDFA' },
  blue:     { name: 'Blue',     primary: '#3B82F6', hover: '#2563EB', text: '#1D4ED8', tint: '#EFF6FF' },
  indigo:   { name: 'Indigo',   primary: '#6366F1', hover: '#4F46E5', text: '#4338CA', tint: '#EEF2FF' },
  violet:   { name: 'Violet',   primary: '#8B5CF6', hover: '#7C3AED', text: '#6D28D9', tint: '#F5F3FF' },
  rose:     { name: 'Rose',     primary: '#F43F5E', hover: '#E11D48', text: '#BE123C', tint: '#FFF1F2' },
  amber:    { name: 'Amber',    primary: '#F59E0B', hover: '#D97706', text: '#B45309', tint: '#FFFBEB' },
  graphite: { name: 'Graphite', primary: '#1F2937', hover: '#111827', text: '#1F2937', tint: '#F3F4F6' },
};

// Active language picker - English + Persian.
export const LANGS: { code: Lang; native: string; english: string; dir: Dir; font: string }[] = [
  { code: 'en', native: 'English', english: 'English', dir: 'ltr', font: '"Inter", system-ui, sans-serif' },
  { code: 'fa', native: 'فارسی',  english: 'Persian', dir: 'rtl', font: '"Vazirmatn", system-ui, sans-serif' },
];

const STORAGE_KEY = 'client-settings';

interface V4Settings {
  lang: Lang | null; // null = not picked yet → show splash language picker
  fontSize: FontSize;
  accent: Accent;
  themeMode: ThemeMode; // default LIGHT for client users
}

const defaults: V4Settings = { lang: 'en', fontSize: 'normal', accent: 'green', themeMode: 'light' };

// ─────────────────────────────────────────────────────────────────────────────
// Translation strings. New keys go here; missing keys fall back to the EN value.
// ─────────────────────────────────────────────────────────────────────────────
const STRINGS: Record<string, Record<Lang, string>> = {
  // App
  app_name:          { en: 'RoniDoc',                  fa: 'رونی‌داک' },
  app_tagline:       { en: 'Your business. Your accountant. In your pocket.',
                       fa: 'کسب و کار شما. حسابدار شما. در جیب شما.' },
  pick_language:     { en: 'Choose your language',     fa: 'زبان خود را انتخاب کنید' },
  continue:          { en: 'Continue',                 fa: 'ادامه' },
  business_name:     { en: 'James Tiling Pty Ltd',     fa: 'شرکت جیمز تایلینگ' },
  firm_name:         { en: 'United Accountants Group',  fa: 'گروه متحد حسابداران' },
  firm_initials:     { en: 'UAG',                       fa: 'UAG' },
  home_ask_label:    { en: 'Ask',                       fa: 'بپرس' },
  home_ask_sub:      { en: 'AI',                        fa: 'هوش مصنوعی' },
  home_biz_label:    { en: 'My',                        fa: 'کسب' },
  home_biz_sub:      { en: 'Business',                  fa: 'و کارم' },
  home_docs_label:   { en: 'Docs',                      fa: 'اسناد' },
  home_docs_sub:     { en: 'Access',                    fa: 'دسترسی' },
  powered_by_ronidoc:{ en: 'Powered by',                fa: 'با همکاری' },

  // Bottom nav
  nav_today:         { en: 'Home',                     fa: 'خانه' },
  nav_ask:           { en: 'Ask AI',                   fa: 'بپرس از AI' },
  nav_money:         { en: 'My Business',              fa: 'کسب و کار من' },
  nav_files:         { en: 'Docs',                     fa: 'اسناد' },
  nav_letters:       { en: 'Letters',                  fa: 'نامه‌ها' },
  nav_receipts:      { en: 'Receipts',                 fa: 'رسیدها' },

  // Files
  files_title:       { en: 'My Files',                 fa: 'فایل‌های من' },
  files_subtitle:    { en: 'All documents prepared by your accountant',
                       fa: 'تمام اسناد آماده‌شده توسط حسابدار شما' },
  search_files:      { en: 'Search files…',            fa: 'جستجوی فایل‌ها…' },
  category_all:      { en: 'All',                      fa: 'همه' },
  category_sent:     { en: 'Sent',                     fa: 'فرستاده شده' },
  category_tax:      { en: 'Tax returns',              fa: 'اظهارنامه‌های مالیاتی' },
  category_bas:      { en: 'BAS',                      fa: 'BAS' },
  category_financials: { en: 'Financials',             fa: 'صورت‌های مالی' },
  category_letters:  { en: 'Letters',                  fa: 'نامه‌ها' },
  category_receipts: { en: 'Receipts',                 fa: 'رسیدها' },
  category_other:    { en: 'Other',                    fa: 'سایر' },
  filter_year:       { en: 'Year',                     fa: 'سال' },
  all_years:         { en: 'All years',                fa: 'همه سال‌ها' },
  prepared_by:       { en: 'Prepared by',              fa: 'آماده شده توسط' },
  pages_count:       { en: '{n} pages',                fa: '{n} صفحه' },
  ask_about_file:    { en: 'Ask about this file',      fa: 'درباره این فایل بپرس' },
  download:          { en: 'Download',                 fa: 'دانلود' },
  share:             { en: 'Share',                    fa: 'اشتراک' },
  no_files_match:    { en: 'No files match your search', fa: 'فایلی با این جستجو پیدا نشد' },
  add_file:          { en: 'Add a file',               fa: 'افزودن فایل' },
  add_file_hint:     { en: 'Send by email, WhatsApp or photo',
                       fa: 'با ایمیل، واتساپ یا عکس بفرست' },
  ask_about_this:    { en: 'Ask about this',           fa: 'درباره این بپرس' },
  back_to_files:     { en: 'Back to files',            fa: 'بازگشت به فایل‌ها' },

  // Today (Home)
  hello:             { en: 'Hello',                    fa: 'سلام' },
  this_week:         { en: 'This week',                fa: 'این هفته' },
  this_month:        { en: 'This month',               fa: 'این ماه' },
  this_year:         { en: 'This year',                fa: 'امسال' },
  money_you_made:    { en: 'Money you made',           fa: 'پولی که در آوردید' },
  money_you_spent:   { en: 'Money you spent',          fa: 'پولی که خرج کردید' },
  money_you_saved:   { en: 'Money you saved',          fa: 'پولی که پس انداز کردید' },
  above_average:     { en: 'above your average',       fa: 'بالاتر از میانگین شما' },
  below_average:     { en: 'below your average',       fa: 'پایین‌تر از میانگین شما' },
  you_are_up_to_date:{ en: 'You are up to date',       fa: 'همه چیز مرتب است' },
  no_action_needed:  { en: 'No tax owing. No overdue lodgements.', fa: 'مالیات معوقه ندارید. هیچ کاری انجام ندهید.' },
  action_needed:     { en: 'Action needed',            fa: 'نیاز به اقدام' },
  next_tax_deadline: { en: 'Next tax deadline',        fa: 'مهلت بعدی مالیات' },
  in_days:           { en: 'in {days} days',           fa: 'تا {days} روز' },
  your_accountant:   { en: 'Your accountant',          fa: 'حسابدار شما' },
  tap_to_call:       { en: 'Tap to call',              fa: 'برای تماس لمس کنید' },
  tip_of_the_day:    { en: 'Tip of the day',           fa: 'نکته‌ی روز' },
  see_more_tips:     { en: 'See more tips',            fa: 'نکات بیشتر' },
  ask_anything:      { en: 'Ask anything',             fa: 'هر چی می‌خوای بپرس' },
  recent_letter:     { en: 'Recent letter',            fa: 'نامه اخیر' },
  overview:          { en: 'Overview',                 fa: 'نمای کلی' },
  revenue:           { en: 'Revenue',                  fa: 'درآمد' },
  expenses:          { en: 'Expenses',                 fa: 'هزینه‌ها' },
  profit:            { en: 'Profit',                   fa: 'سود' },
  margin:            { en: 'Margin',                   fa: 'حاشیه' },
  vs_last_period:    { en: 'vs last period',           fa: 'نسبت به دوره قبل' },
  cash_in_bank:      { en: 'Cash in bank',             fa: 'موجودی بانکی' },
  outstanding_invoices: { en: 'Owed to you',           fa: 'بدهکار به شما' },
  bills_to_pay:      { en: 'You owe',                  fa: 'بدهی شما' },
  whats_due:         { en: 'What\'s due',              fa: 'موارد در پیش' },
  briefing:          { en: 'Today\'s briefing',        fa: 'گزارش امروز' },
  view_details:      { en: 'View details',             fa: 'مشاهده جزئیات' },
  view_report:       { en: 'View full report',         fa: 'مشاهده گزارش کامل' },
  call:              { en: 'Call',                     fa: 'تماس' },
  message:           { en: 'Message',                  fa: 'پیام' },
  bas_due_in_days:   { en: 'BAS due in {days} days',   fa: 'مهلت BAS تا {days} روز' },
  draft_ready:       { en: 'Draft ready',              fa: 'پیش‌نویس آماده' },

  // Money / Insights expanded
  revenue_profit_trend: { en: 'Revenue & profit',      fa: 'درآمد و سود' },
  last_6_months:     { en: 'Last 6 months',            fa: '۶ ماه گذشته' },
  revenue_mix:       { en: 'Revenue mix',              fa: 'ترکیب درآمد' },
  by_service_line:   { en: 'By service line - current FY', fa: 'به تفکیک خدمات - سال جاری' },
  cashflow_health:   { en: 'Cashflow health',          fa: 'سلامت جریان نقدی' },
  cashflow_hint:     { en: 'Based on receivables, payables and 90-day runway',
                       fa: 'بر اساس مطالبات، بدهی‌ها و ۹۰ روز جریان' },
  healthy:           { en: 'Healthy',                  fa: 'سالم' },
  days_runway:       { en: 'Days of runway',           fa: 'روزهای دوام' },
  outstanding_inv:   { en: 'Outstanding invoices',     fa: 'فاکتورهای پرداخت‌نشده' },
  bills_due_30d:     { en: 'Bills due 30d',            fa: 'بدهی ۳۰ روز آینده' },
  where_money_goes:  { en: 'Where your money goes',    fa: 'پولت کجا میره' },
  top_expenses:      { en: 'Top expenses this FY',     fa: 'بیشترین هزینه‌های امسال' },
  worth_discussing:  { en: 'Worth discussing',         fa: 'ارزش بحث دارد' },
  worth_discussing_hint: { en: 'Possible deductions based on your industry',
                       fa: 'کسرهای احتمالی بر اساس صنعت شما' },
  tips_general_guidance: { en: 'Tips are general guidance. Your accountant can confirm what applies to you.',
                       fa: 'این نکات راهنمای عمومی است. حسابدارت می‌تواند تایید کند.' },
  how_you_compare:   { en: 'How you compare',          fa: 'مقایسه با دیگران' },
  vs_similar_biz:    { en: 'Versus similar businesses in your industry',
                       fa: 'نسبت به کسب‌وکارهای مشابه در صنعت شما' },
  above_avg:         { en: 'Above average',            fa: 'بالای میانگین' },
  below_avg:         { en: 'Below average',            fa: 'زیر میانگین' },
  you:               { en: 'You',                      fa: 'شما' },
  avg:               { en: 'Avg',                      fa: 'م.ا' },

  // Files extras
  snap_receipt:      { en: 'Send to your accountant',  fa: 'برای حسابدارت بفرست' },
  snap_hint:         { en: 'Receipts, letters, IDs, voice notes, questions - all in one place',
                       fa: 'رسیدها، نامه‌ها، مدارک، پیام صوتی، سوال - همه یکجا' },
  send_tagline:      { en: 'Use this instead of WhatsApp, SMS or email - nothing gets lost.',
                       fa: 'به جای واتساپ، پیامک یا ایمیل از اینجا استفاده کن - هیچ چیز گم نمی‌شود.' },
  send_response_time:{ en: 'We see it instantly. {firm} usually replies within 2 hours on business days.',
                       fa: 'فوراً می‌بینیم. {firm} معمولاً در ساعات کاری ظرف ۲ ساعت جواب می‌دهد.' },
  send_now:          { en: 'Send now',                  fa: 'الان بفرست' },
  opt_camera:        { en: 'Take a photo',              fa: 'عکس بگیر' },
  opt_camera_hint:   { en: 'Receipt, letter, ID - anything', fa: 'رسید، نامه، مدرک - هر چیزی' },
  opt_upload:        { en: 'Upload a file',             fa: 'فایل آپلود کن' },
  opt_upload_hint:   { en: 'PDF, Word, photos from your device', fa: 'PDF، Word، عکس از دستگاه' },
  opt_voice:         { en: 'Record a voice note',       fa: 'پیام صوتی ضبط کن' },
  opt_voice_hint:    { en: 'Faster than typing - up to 2 minutes', fa: 'سریعتر از تایپ - تا ۲ دقیقه' },
  opt_message:       { en: 'Type a message',            fa: 'پیام بنویس' },
  opt_message_hint:  { en: 'Quick question or note',    fa: 'سوال یا یادداشت سریع' },
  more_channels:     { en: 'Other ways to send',        fa: 'راه‌های دیگر' },
  recording:         { en: 'Recording…',                fa: 'در حال ضبط…' },
  type_your_message: { en: 'Type your message…',        fa: 'پیامت رو بنویس…' },
  message_sent:      { en: 'Sent! Your accountant will see it shortly.', fa: 'فرستاده شد! حسابدارت به زودی می‌بیند.' },

  // Messages / inbox
  messages_title:    { en: 'Messages from your firm', fa: 'پیام‌ها از حسابدارت' },
  messages_empty:    { en: 'No new messages',         fa: 'پیام جدیدی نیست' },
  mark_all_read:     { en: 'Mark all as read',        fa: 'همه را خوانده علامت بزن' },
  type_personal:     { en: 'From your accountant',    fa: 'از حسابدارت' },
  type_update:       { en: 'Update',                  fa: 'به‌روزرسانی' },
  type_news:         { en: 'News',                    fa: 'اخبار' },
  type_voice:        { en: 'Voice note',               fa: 'پیام صوتی' },
  requested_files:   { en: 'Files your accountant requested',
                       fa: 'فایل‌هایی که حسابدارت درخواست کرده' },
  pending:           { en: 'pending',                  fa: 'در انتظار' },
  upload:            { en: 'Upload',                   fa: 'آپلود' },
  uploaded_just_now: { en: 'Uploaded just now',        fa: 'همین الان آپلود شد' },
  preview:           { en: 'Preview',                  fa: 'پیش‌نمایش' },
  pdf_preview:       { en: 'PDF preview',              fa: 'پیش‌نمایش PDF' },
  open:              { en: 'Open',                     fa: 'باز کن' },
  tip_of_day_body:   { en: 'Working from home? Claim 70¢ per hour for power, internet and phone - no receipts needed.',
                       fa: 'از خانه کار می‌کنی؟ هر ساعت ۷۰ سنت بابت برق، اینترنت و تلفن کسر کن - بدون نیاز به رسید.' },

  // Ask
  ask_title:         { en: 'Ask a question',           fa: 'سوال بپرس' },
  ask_hint:          { en: 'AI tries to answer first. If it can\'t, your accountant gets a message.',
                       fa: 'هوش مصنوعی اول جواب می‌دهد. اگر نتوانست، حسابدارت پیام دریافت می‌کند.' },
  type_question:     { en: 'Type your question…',      fa: 'سوال خود را بنویس…' },
  send:              { en: 'Send',                     fa: 'بفرست' },
  ask_marcus_instead:{ en: 'Ask {firm} instead',         fa: 'به جای آن از {firm} بپرس' },
  forwarded_to_accountant: { en: 'Sent to your accountant. They will reply soon.',
                       fa: 'برای حسابدارت ارسال شد. به زودی جواب می‌دهد.' },

  // Money
  money_title:       { en: 'Money',                    fa: 'پول' },
  in:                { en: 'In',                       fa: 'وارد' },
  out:               { en: 'Out',                      fa: 'خارج' },
  top_things_you_spent_on: { en: 'What you spent the most on',
                       fa: 'بیشترین خرجت برای چی بود' },

  // Letters
  letters_title:     { en: 'Letters & ATO mail',       fa: 'نامه‌ها و مکاتبات اداره مالیات' },
  upload_letter:     { en: 'Take photo of a letter',   fa: 'از نامه عکس بگیر' },
  letter_hint:       { en: 'We will read it for you and explain in your language.',
                       fa: 'برات می‌خوانیم و به زبان خودت توضیح می‌دیم.' },
  letter_translated_status: { en: 'Translated',        fa: 'ترجمه شد' },
  letter_pending_status: { en: 'Being read',           fa: 'در حال خواندن' },

  // Receipts
  receipts_title:    { en: 'Receipts',                 fa: 'رسیدها' },
  receipts_intro:    { en: 'Send us your receipts and we will file them automatically.',
                       fa: 'رسیدهات رو برامون بفرست تا اتوماتیک ثبتشون کنیم.' },
  your_email_address:{ en: 'Your receipts email',      fa: 'ایمیل رسیدهای شما' },
  forward_emails_here: { en: 'Forward supplier receipts to this address',
                       fa: 'ایمیل‌های فاکتور رو به این آدرس بفرست' },
  whatsapp_send:     { en: 'Or send photos by WhatsApp', fa: 'یا با واتساپ عکس بفرست' },
  copy:              { en: 'Copy',                     fa: 'کپی' },
  copied:            { en: 'Copied',                   fa: 'کپی شد' },
  recent_receipts:   { en: 'Recent receipts',          fa: 'رسیدهای اخیر' },

  // Tips
  tips_title:        { en: 'Tax tips for shopkeepers', fa: 'نکات مالیاتی برای صاحبان دکان' },

  // Settings
  settings:          { en: 'Settings',                 fa: 'تنظیمات' },
  language:          { en: 'Chat Language',            fa: 'زبان گفتگو' },
  text_size:         { en: 'Text size',                fa: 'اندازه‌ی نوشته' },
  text_normal:       { en: 'Normal',                   fa: 'معمولی' },
  text_large:        { en: 'Large',                    fa: 'بزرگ' },
  text_xl:           { en: 'Extra large',              fa: 'خیلی بزرگ' },
  about:             { en: 'About',                    fa: 'درباره' },
  powered_by:        { en: 'Powered by',               fa: 'با همکاری' },
  log_out:           { en: 'Log out',                  fa: 'خروج' },

  // Misc
  good:              { en: 'Good',                     fa: 'خوب' },
  warning:           { en: 'Heads up',                 fa: 'توجه' },
  appearance:        { en: 'Appearance',               fa: 'ظاهر' },
  theme_light:       { en: 'Light',                    fa: 'روشن' },
  theme_dark:        { en: 'Dark',                     fa: 'تیره' },
};

// Lookup with EN fallback.
function lookup(key: string, lang: Lang): string {
  const entry = STRINGS[key];
  if (!entry) return key;
  return entry[lang] || entry.en || key;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────
interface V4Ctx {
  settings: V4Settings;
  setLang: (l: Lang) => void;
  setFontSize: (s: FontSize) => void;
  setAccent: (a: Accent) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  dir: Dir;
  font: string;
  fmtCurrency: (n: number) => string;
  themeMode: ThemeMode;
  setThemeMode: (m: ThemeMode) => void;
  accent: { primary: string; hover: string; text: string; tint: string };
}

const Ctx = createContext<V4Ctx | null>(null);

export function V4SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<V4Settings>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch {}
    return defaults;
  });

  // UI is locked to English. The user's `settings.lang` is now ONLY a hint
  // for the chat AI (currently no-op - the AI auto-detects question
  // language at reply time). The picker stays for that future use, but
  // selecting Persian no longer translates menus, flips the layout to
  // RTL, or swaps fonts - the user explicitly asked for this.
  const lang: Lang = 'en';
  const langDef = LANGS[0]; // English (LTR, Inter font)

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch {}
  }, [settings]);

  // Sync <html> class to v4's themeMode so Tailwind dark variants behave
  // correctly across both the Layout-wrapped routes AND the standalone
  // LoginPage (which doesn't mount the Layout). Owns the html class while
  // any v4 page is mounted; restores prior on unmount so the firm panel
  // isn't left in the wrong mode.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.classList.contains('dark') ? 'dark' : 'light';
    html.classList.remove('light', 'dark');
    html.classList.add(settings.themeMode);
    return () => {
      html.classList.remove('light', 'dark');
      html.classList.add(prev);
    };
  }, [settings.themeMode]);

  // Font-size scaling. Tailwind text utilities (text-sm, text-xs, etc.) are
  // rem-based and computed against the <html> root font-size - so the only
  // reliable way to scale the whole UI is to set fontSize on documentElement.
  // A wrapper div with text-[118%] doesn't propagate because rem is global,
  // not inherited.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.fontSize;
    const px =
      settings.fontSize === 'xl'    ? '20px' :   // 125% of default 16px
      settings.fontSize === 'large' ? '18px' :   // 112.5%
      '16px';                                    // normal
    html.style.fontSize = px;
    return () => { html.style.fontSize = prev; };
  }, [settings.fontSize]);

  useEffect(() => {
    document.documentElement.dir = langDef.dir;
    document.documentElement.lang = lang;
    // Inject Vazirmatn font if needed (one-time)
    if (langDef.dir === 'rtl' && !document.getElementById('vazirmatn-font')) {
      const link = document.createElement('link');
      link.id = 'vazirmatn-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Vazirmatn:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
    return () => {
      // restore on unmount so other pages aren't stuck in RTL
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
    };
  }, [lang, langDef.dir]);

  const t = (key: string, vars?: Record<string, string | number>) => {
    let s = lookup(key, lang);
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
    return s;
  };

  const fmtCurrency = (n: number) => {
    // Always AUD; numerals stay Hindu-Arabic so shopkeepers reading any
    // language can recognise digits.
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
  };

  const accentDef = ACCENTS[settings.accent] || ACCENTS.green;

  return (
    <Ctx.Provider value={{
      themeMode: settings.themeMode,
      setThemeMode: (m: ThemeMode) => setSettings(s => ({ ...s, themeMode: m })),
      settings,
      setLang: (l) => setSettings(s => ({ ...s, lang: l })),
      setFontSize: (size) => setSettings(s => ({ ...s, fontSize: size })),
      setAccent: (a) => setSettings(s => ({ ...s, accent: a })),
      accent: accentDef,
      t,
      dir: langDef.dir,
      font: langDef.font,
      fmtCurrency,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useV4() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useV4 must be inside V4SettingsProvider');
  return v;
}
